// Basic
import { useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import actionsSims from '../../../api/actionsSims';
// Components
import OrganizationsTable from '@/app/modules/organizations/components/tables/OrganizationsTable';
import SimCardsTable from '../../tables/SimCardsTable';
import FooterStep from '@/common-utils/components/renders/modal/components/footers/FooterStep/FooterStep';
import Entity from '@/common-utils/components/renders/entity/Entity';
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import CustomPaySelectOffer from '../../forms/CustomPaySelectOffer';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
// Common
import getInitialValueForRowSelected from '@/common-utils/components/renders/dataTable/utils/GetInitialValueForRowSelected';
import simCardFields from '../../../fields/simCardFields';
import generateNestedFieldAccessor from '@/common-utils/functions/generateNestedFieldAccessor';
import organizationFields from '@/app/modules/organizations/fields/organizationFields';
import ORGANIZATION_TYPES from '@/app/modules/organizations/enums/OrganizationTypes';
import { useToast } from '@/common-utils/hooks/use-toast';
import useStepConfig from '@/common-utils/hooks/useStepConfig';
// Lang
import { nsSimCard } from '@/app/i18n/namespaces';
import setFiltersEventName from '@/common-utils/components/renders/dataTable/utils/setFiltersEventName';

/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @param root0.setLaunchRefetch
 * @returns {JSX} CustomPayModalSimsMultiActivation
 */
function CustomPayModalSimsMultiActivation({ isOpen, setIsOpen, setLaunchRefetch }) {

    // Toast
    const { toast } = useToast();

    // Constants
    const STEPS_NUMBER = 4;
    const STEP_TITLE_PREFIX = 'modal.custom_pay_multiple_activation.steps';

    // State
    const [step, setStep] = useState(0);
    const [clientSelected, setClientSelected] = useState(getInitialValueForRowSelected);
    const [simCardsSelected, setSimCardsSelected] = useState(getInitialValueForRowSelected);
    const [selectedOffer, setSelectedOffer] = useState('');
    const [selectedOfferName, setSelectedOfferName] = useState('');

    // API Calls
    // Mutations
    const onSuccess = () => {
        setIsOpen(false);
        setLaunchRefetch(true);
        toast({
            description: <Entity entity='modal.custom_pay_multiple_activation.success' namespace={nsSimCard} />,
            variant: 'success'
        });
    };
    const onError = () => {
        setIsOpen(false);
        toast({
            description: <Entity entity='modal.custom_pay_multiple_activation.error' namespace={nsSimCard} />,
            variant: 'danger'
        });
    };
    const submitMultiActivatePayviewSimCards = actionsSims.multiActivatePayviewSimCards(onSuccess, onError);

    // Hooks
    const { stepTitle, disabledNext } = useStepConfig(
        step,
        () => ({
            0: { stepTitle: `${STEP_TITLE_PREFIX}.client_selection`, disabledNext: !clientSelected.rowsData.length },
            1: { stepTitle: `${STEP_TITLE_PREFIX}.sims_selection`, disabledNext: !simCardsSelected.rowsData.length },
            2: { stepTitle: `${STEP_TITLE_PREFIX}.offer_selection`, disabledNext: selectedOffer === '' && selectedOfferName === '' },
            3: { stepTitle: `${STEP_TITLE_PREFIX}.confirmation`, disabledNext: false }
        }),
        [clientSelected, simCardsSelected, selectedOffer, selectedOfferName]
    );

    // Render
    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className={step === 0 || step === 1 ? 'max-w-6xl' : 'max-w-lg'}>
                <HeaderTitle title='modal.custom_pay_multiple_activation.title' namespace={nsSimCard} />
                <ModalBody className='modal-max-height overflow-auto'>
                    <Entity
                        entity={stepTitle}
                        data={{ step: step + 1, maxStep: STEPS_NUMBER }}
                        namespace={nsSimCard}
                        className='mb-2 flex'
                    />
                    {
                        step === 0 &&
                        <OrganizationsTable
                            filtersEventName={setFiltersEventName('organizations', true)}
                            customQuery={{
                                filters: [
                                    {
                                        key: organizationFields.TYPE.key,
                                        value: ORGANIZATION_TYPES.WHITE_LABEL.name,
                                        hidden: true
                                    }
                                ]
                            }}
                            singleSelection={true}
                            rowsSelected={clientSelected}
                            setRowsSelected={setClientSelected}
                        />
                    }
                    {
                        step === 1 &&
                        <SimCardsTable
                            filtersEventName={setFiltersEventName('sim_cards', true)}
                            organizationId={clientSelected.rowsData[0].uuid}
                            customQuery={{
                                filters: [
                                    {
                                        key: generateNestedFieldAccessor(simCardFields.SIM_CARD.STATUS),
                                        value: 'ACTIVABLE',
                                        hidden: true
                                    }
                                ]
                            }}
                            rowsSelected={simCardsSelected}
                            setRowsSelected={setSimCardsSelected}
                        />
                    }
                    {
                        step === 2 &&
                        <CustomPaySelectOffer
                            selectedOffer={selectedOffer}
                            setSelectedOffer={setSelectedOffer}
                            setSelectedOfferName={setSelectedOfferName}
                        />
                    }
                    {
                        step === 3 &&
                        <Entity
                            entity='modal.custom_pay_multiple_activation.text.confirmation'
                            data={{
                                simNumber: Object.keys(simCardsSelected.rowIds).length,
                                offer: selectedOfferName
                            }}
                            namespace={nsSimCard}
                        />
                    }
                </ModalBody>
                <FooterStep
                    step={step}
                    setStep={setStep}
                    stepsNumber={STEPS_NUMBER}
                    onValidate={() => {
                        submitMultiActivatePayviewSimCards.mutate({
                            offer: selectedOffer,
                            uuids: simCardsSelected.rowsData.map((item) => item.sim.uuid)
                        });
                    }}
                    isLoading={submitMultiActivatePayviewSimCards.isLoading}
                    disableValidation={disabledNext}
                />
            </ModalContent>
        </Modal>
    );
}

CustomPayModalSimsMultiActivation.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    setLaunchRefetch: PropTypes.func.isRequired
};

export default CustomPayModalSimsMultiActivation;